import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "../App";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { 
  LayoutDashboard, 
  Receipt, 
  ShoppingCart,
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  Wallet,
  Download,
  Smartphone,
  Share,
  MoreVertical,
  Package,
  Eye,
  CreditCard,
  Tag,
  Moon,
  Sun,
  Boxes,
  KeyRound,
  Building2
} from "lucide-react";

const LOGO_URL = "https://hosting.photobucket.com/0527623e-3ddc-48a1-b0eb-b299d3c19e95/b8990f2e-2369-4dcc-8414-e8227f89b88d.png";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);

  // PWA Install prompt handler
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt) {
      // Si hay prompt disponible, instalar automáticamente
      installPrompt.prompt();
      await installPrompt.userChoice;
      // No limpiamos el prompt para que el botón siga funcionando
    } else {
      // Solo mostrar instrucciones si no hay prompt disponible
      setShowInstallHelp(true);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isAdmin = user?.role === "admin";
  const isSupplier = user?.role === "proveedor";
  const isGerenteMerch = user?.role === "gerente_merch";
  const isGerenteHeladeria = user?.role === "gerente_heladeria" || user?.role === "manager";
  const canEditInventory = !!user?.can_edit_inventory;

  // Supplier navigation
  const supplierNavItems = [
    { path: "/supplier", label: "Mi Dashboard", icon: Package },
  ];

  // Admin/Manager navigation - organized by sections
  const heladeriaSection = {
    title: "HELADERÍA",
    items: [
      { path: "/", label: "Dashboard", icon: LayoutDashboard },
      { path: "/transactions", label: "Gastos", icon: Receipt },
      { path: "/sales", label: "Ventas", icon: ShoppingCart },
    ]
  };

  const merchSection = {
    title: "MERCH",
    items: [
      { path: "/merch", label: "Dashboard", icon: LayoutDashboard },
      { path: "/merch/products", label: "Productos", icon: Tag },
      { path: "/merch/inventory", label: "Inventario", icon: Boxes },
      { path: "/merch/costs", label: "Costos/Lotes", icon: Package },
      { path: "/merch/expenses", label: "Gastos", icon: Receipt },
    ]
  };

  const finanzasSection = {
    title: "FINANZAS",
    items: [
      { path: "/personal-finance", label: "Personales", icon: Wallet },
      { path: "/account-expenses", label: "Cuenta", icon: CreditCard },
    ]
  };

  const seguridadSection = {
    title: "SEGURIDAD",
    items: [
      { path: "/credentials", label: "Contraseñas", icon: KeyRound },
    ]
  };

  const adminSection = {
    title: null, // No title, just items
    items: [
      { path: "/user-view", label: "Vista Usuario", icon: Eye },
      { path: "/branches", label: "Sucursales", icon: Building2 },
      { path: "/settings", label: "Configuración", icon: Settings },
    ]
  };

  const managerSection = {
    title: null,
    items: [
      { path: "/settings", label: "Configuración", icon: Settings },
    ]
  };

  // Build sections based on role
  let navSections;
  if (isSupplier) {
    navSections = [{ title: null, items: supplierNavItems }];
  } else if (isAdmin) {
    // Admin sees everything
    navSections = [heladeriaSection, merchSection, finanzasSection, seguridadSection, adminSection];
  } else if (isGerenteMerch) {
    // Gerente Merch only sees Merch section + Settings + Security
    navSections = [merchSection, seguridadSection, managerSection];
  } else {
    // Gerente Heladería only sees Heladería section + Settings + Security
    // Plus an Inventario section if admin granted them can_edit_inventory
    const heladeriaSections = [heladeriaSection];
    if (canEditInventory) {
      heladeriaSections.push({
        title: "INVENTARIO",
        items: [
          { path: "/merch/inventory", label: "Inventario", icon: Boxes },
        ],
      });
    }
    navSections = [...heladeriaSections, seguridadSection, managerSection];
  }

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <Link
        to={item.path}
        onClick={() => mobile && setSidebarOpen(false)}
        data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
          isActive 
            ? "bg-[#F43F5E] text-white shadow-md" 
            : "text-[#6B7280] hover:bg-[#FEE2E2] hover:text-[#4A4A4A]"
        }`}
      >
        <Icon className="w-5 h-5" />
        <span className="font-medium">{item.label}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFF5F5] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#FFE4E6] border-r border-[#FECDD3] h-screen sticky top-0 overflow-hidden">
        {/* Logo */}
        <div className="p-6 border-b border-[#FECDD3] flex-shrink-0">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Darkar Co." className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-[#4A4A4A] font-bold font-['Manrope']">Darkar Co.</h1>
              <p className="text-[#6B7280] text-xs">Control de Ventas</p>
            </div>
          </Link>
        </div>

        {/* Navigation with Sections - Scrollable */}
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto min-h-0 pb-2">
          {navSections.map((section, sIdx) => (
            <div key={sIdx}>
              {section.title && (
                <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2 px-3">
                  {section.title}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink key={item.path} item={item} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User - Fixed at bottom */}
        <div className="p-4 border-t border-[#FECDD3] flex-shrink-0 bg-[#FFE4E6]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#F43F5E] flex items-center justify-center text-white font-bold shadow-md">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#4A4A4A] text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[#6B7280] text-xs truncate">{user?.email}</p>
            </div>
          </div>
          
          {/* Dark Mode Toggle */}
          <Button
            variant="ghost"
            onClick={toggleDarkMode}
            data-testid="dark-mode-toggle"
            className="w-full justify-start text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#FEE2E2] mb-1"
          >
            {isDarkMode ? (
              <>
                <Sun className="w-4 h-4 mr-2" />
                Modo Claro
              </>
            ) : (
              <>
                <Moon className="w-4 h-4 mr-2" />
                Modo Oscuro
              </>
            )}
          </Button>
          
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#FEE2E2]"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
          
          {/* Install App Button - Always visible */}
          <Button
            onClick={handleInstallClick}
            data-testid="install-app-button"
            className="w-full mt-2 bg-gradient-to-r from-[#F43F5E] to-[#EC4899] hover:from-[#E11D48] hover:to-[#DB2777] text-white shadow-md"
          >
            <Download className="w-4 h-4 mr-2" />
            Instalar App
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`lg:hidden fixed inset-y-0 left-0 w-64 bg-[#FFE4E6] border-r border-[#FECDD3] z-50 transform transition-transform flex flex-col overflow-hidden ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-[#FECDD3] flex items-center justify-between flex-shrink-0">
          <Link to="/" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
            <img src={LOGO_URL} alt="Darkar Co." className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-[#4A4A4A] font-bold font-['Manrope']">Darkar Co.</h1>
              <p className="text-[#6B7280] text-xs">Control de Ventas</p>
            </div>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(false)}
            className="text-[#6B7280]"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation with Sections - Mobile */}
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto min-h-0 pb-2">
          {navSections.map((section, sIdx) => (
            <div key={sIdx}>
              {section.title && (
                <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider mb-2 px-3">
                  {section.title}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink key={item.path} item={item} mobile />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-[#FECDD3] flex-shrink-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#F43F5E] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#4A4A4A] text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[#6B7280] text-xs truncate">{user?.email}</p>
            </div>
          </div>
          
          {/* Dark Mode Toggle - Mobile */}
          <Button
            variant="ghost"
            onClick={toggleDarkMode}
            className="w-full justify-start text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#FEE2E2] mb-1"
          >
            {isDarkMode ? (
              <>
                <Sun className="w-4 h-4 mr-2" />
                Modo Claro
              </>
            ) : (
              <>
                <Moon className="w-4 h-4 mr-2" />
                Modo Oscuro
              </>
            )}
          </Button>
          
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#FEE2E2]"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
          
          {/* Install App Button - Mobile - Always visible */}
          <Button
            onClick={handleInstallClick}
            className="w-full mt-2 bg-gradient-to-r from-[#F43F5E] to-[#EC4899] hover:from-[#E11D48] hover:to-[#DB2777] text-white shadow-md"
          >
            <Download className="w-4 h-4 mr-2" />
            Instalar App
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-[#FECDD3] shadow-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
            className="text-[#6B7280]"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Darkar Co." className="w-10 h-10 object-contain" />
            <span className="text-[#4A4A4A] font-bold font-['Manrope']">Darkar Co.</span>
          </div>
          <div className="w-10" /> {/* Spacer */}
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>

      {/* Install Help Dialog */}
      <Dialog open={showInstallHelp} onOpenChange={setShowInstallHelp}>
        <DialogContent className="bg-white border-[#FECDD3] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#4A4A4A]">
              <Smartphone className="w-5 h-5 text-[#F43F5E]" />
              Instalar Darkar App
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#6B7280] text-sm">
              Instala la app en tu dispositivo para acceder más rápido:
            </p>
            
            <div className="space-y-3">
              <div className="bg-[#FEE2E2] rounded-lg p-3">
                <p className="font-medium text-[#4A4A4A] text-sm flex items-center gap-2">
                  <span className="bg-[#F43F5E] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                  Chrome / Edge (Android/PC)
                </p>
                <p className="text-[#6B7280] text-xs mt-1 ml-7">
                  Toca el menú <MoreVertical className="w-3 h-3 inline" /> → "Instalar app" o "Agregar a inicio"
                </p>
              </div>
              
              <div className="bg-[#FEE2E2] rounded-lg p-3">
                <p className="font-medium text-[#4A4A4A] text-sm flex items-center gap-2">
                  <span className="bg-[#F43F5E] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">2</span>
                  Safari (iPhone/iPad)
                </p>
                <p className="text-[#6B7280] text-xs mt-1 ml-7">
                  Toca <Share className="w-3 h-3 inline" /> Compartir → "Agregar a pantalla de inicio"
                </p>
              </div>
            </div>

            <Button 
              onClick={() => setShowInstallHelp(false)}
              className="w-full bg-[#F43F5E] hover:bg-[#E11D48] text-white"
            >
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
